import { fmtMoneda } from '../../utils/moneda';
import React, { useState, useEffect, useCallback } from 'react';
import { supabase, insertRow, updateRow } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { Plus, X, AlertTriangle, Trash2, Paperclip, Search } from 'lucide-react';
import { fmtFecha, fmtFechaHora } from '../../utils/fecha';
import { abrirDocumento } from '../../utils/archivos';
import ClienteSelect from '../../components/ClienteSelect';
import PolizaSelect from '../../components/PolizaSelect';

const ESTADOS = ['abierto', 'en_proceso', 'en_revision', 'aprobado', 'rechazado', 'cerrado'];
const ESTADO_BADGE = { abierto:'badge-danger', en_proceso:'badge-warning', en_revision:'badge-info', aprobado:'badge-success', rechazado:'badge-neutral', cerrado:'badge-neutral' };
// Motivos de cierre: manual del usuario, o la aseguradora lo pagó / lo declinó.
const MOTIVOS_CIERRE = [['pagado_aseguradora','La aseguradora lo PAGÓ'], ['declinado_aseguradora','La aseguradora lo DECLINÓ'], ['manual','Cierre manual (usuario)']];
const motivoLabel = m => (MOTIVOS_CIERRE.find(x => x[0] === m)?.[1]) || m;
const motivoCorto = { pagado_aseguradora:'pagado', declinado_aseguradora:'declinado', manual:'manual' };
// Tipos de documento de un reclamo.
const TIPOS_DOC = ['Fotos', 'FUD', 'Cédula', 'Licencia', 'Registro vehicular', 'Presupuesto / Cotización', 'Informe del ajustador', 'Denuncia', 'Finiquito', 'Factura', 'Otro'];
// Transiciones de estado permitidas (además de Cerrar/Reabrir/Eliminar). Un reclamo
// APROBADO ya no puede volver a revisión ni ser rechazado; un CERRADO solo se reabre.
const TRANSICIONES = {
  abierto:     ['en_proceso', 'rechazado'],
  en_proceso:  ['en_revision', 'aprobado', 'rechazado'],
  en_revision: ['aprobado', 'rechazado'],
  aprobado:    [],
  rechazado:   [],
  cerrado:     [],
};
const fmt$ = n => (n != null && n !== '') ? fmtMoneda(n) : '—';
const num = v => (v !== '' && v != null && !isNaN(parseFloat(v))) ? parseFloat(v) : null;
// Si el deducible de la cobertura es un número plano ("250"), se usa como monto sugerido.
const deducibleNumerico = txt => { const m = String(txt ?? '').replace(/[^0-9.]/g,''); return (m && !isNaN(parseFloat(m)) && !/%/.test(String(txt))) ? parseFloat(m) : ''; };
const EMPTY = { numero:'', numero_aseguradora:'', poliza_id:'', cliente_id:'', aseguradora_id:'', descripcion:'', fecha_siniestro:'', fecha_esperada:'', ajustador:'', fecha_notificacion:'', estado:'abierto', notas:'' };

export default function Reclamos() {
  const { user, canEdit, agenteScope, esAgenteRestringido } = useApp();
  const [rows, setRows] = useState([]);
  const [filterPoliza, setFilterPoliza] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [polizas, setPolizas] = useState([]);          // todas las pólizas activas (para filtrar por cliente)
  const [polizaCoberturas, setPolizaCoberturas] = useState([]); // coberturas de la póliza elegida
  const [afectadas, setAfectadas] = useState([]);      // coberturas afectadas del reclamo (con montos)
  const [historial, setHistorial] = useState([]);
  const [archivos, setArchivos] = useState([]);        // archivos adjuntos del reclamo (vista)
  const [polizaDocs, setPolizaDocs] = useState([]);    // docs clave de la póliza (Póliza/Condiciones) traídos al reclamo
  const [pendingFiles, setPendingFiles] = useState([]);// archivos a subir al crear: [{file, tipo_doc}]
  const [viewDocTipo, setViewDocTipo] = useState('Otro'); // tipo para archivos que se suben desde la vista
  const [nota, setNota] = useState('');
  const [asegEdit, setAsegEdit] = useState({ ajustador:'', fecha_notificacion:'' }); // ajustador + fecha de notificación (editables en la vista)
  const [saving, setSaving] = useState(false);
  const [cierre, setCierre] = useState(null);          // { motivo, nota } cuando se está cerrando

  const load = useCallback(async () => {
    if (esAgenteRestringido && !agenteScope) return;
    let q = supabase.from('reclamos')
      .select('*, polizas(numero), clientes(nombre), aseguradoras(nombre)')
      .order('creado_en', { ascending:false });
    if (filterEstado) q = q.eq('estado', filterEstado);
    if (filterCliente) q = q.eq('cliente_id', filterCliente);
    if (filterPoliza) q = q.eq('poliza_id', filterPoliza);
    if (esAgenteRestringido) q = q.in('poliza_id', agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]);
    const { data } = await q;
    setRows((data || []).map(r => ({
      ...r, poliza_num:r.polizas?.numero, cliente_nombre:r.clientes?.nombre, aseguradora_nombre:r.aseguradoras?.nombre
    })));
  }, [filterPoliza, filterEstado, filterCliente, esAgenteRestringido, agenteScope]);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    const { data } = await supabase.from('polizas')
      .select('id, numero, cliente_id, aseguradora_id, clientes(nombre)').eq('estado','activa').order('numero');
    setPolizas((data || []).map(p => ({ id:p.id, numero:p.numero, cliente_id:p.cliente_id, aseguradora_id:p.aseguradora_id, cliente:p.clientes?.nombre })));
    const numero = `REC-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;
    setForm({ ...EMPTY, numero });
    setPolizaCoberturas([]); setAfectadas([]); setPendingFiles([]); setPolizaDocs([]);
    setModal('create');
  };

  // Coberturas de una póliza (para elegir las afectadas).
  const cargarCoberturasPoliza = async (polizaId) => {
    const { data } = await supabase.from('poliza_coberturas').select('id, nombre, deducible, suma_asegurada').eq('poliza_id', polizaId).order('id');
    setPolizaCoberturas(data || []);
  };

  const openView = async (r) => {
    setSelected(r); setCierre(null);
    setAsegEdit({ ajustador: r.ajustador || '', fecha_notificacion: r.fecha_notificacion || '' });
    const [{ data: hist }, { data: cobs }, { data: arch }] = await Promise.all([
      supabase.from('reclamos_historial').select('*, usuarios(nombre)').eq('reclamo_id', r.id).order('creado_en', { ascending:false }),
      supabase.from('reclamo_coberturas').select('*').eq('reclamo_id', r.id).order('id'),
      supabase.from('reclamo_archivos').select('*').eq('reclamo_id', r.id).order('id'),
    ]);
    setHistorial((hist || []).map(h => ({ ...h, usuario:h.usuarios?.nombre })));
    setAfectadas((cobs || []).map(c => ({ ...c, monto_reclamado: c.monto_reclamado ?? '', deducible_monto: c.deducible_monto ?? '', monto_aprobado: c.monto_aprobado ?? '' })));
    setArchivos(arch || []);
    cargarDocsPoliza(r.poliza_id);
    setModal('view');
  };

  // Cliente elegido → filtra pólizas de ese cliente y limpia la selección de póliza.
  const onCliente = (cid) => {
    setForm(f => ({ ...f, cliente_id: cid, poliza_id:'', aseguradora_id:'' }));
    setPolizaCoberturas([]); setAfectadas([]); setPolizaDocs([]);
  };
  const onPoliza = (id) => {
    const p = polizas.find(x => String(x.id) === String(id));
    setForm(f => ({ ...f, poliza_id:id, aseguradora_id: p?.aseguradora_id || '', cliente_id: p?.cliente_id || f.cliente_id }));
    setAfectadas([]);
    if (id) { cargarCoberturasPoliza(id); cargarDocsPoliza(id); } else { setPolizaCoberturas([]); setPolizaDocs([]); }
  };

  // Trae los documentos CLAVE de la póliza (Póliza / Condiciones Generales / Condiciones
  // Particulares) para tenerlos a mano en el reclamo. Solo los que ESTÉN cargados en la
  // póliza; si no hay, no trae nada. Es por referencia (no duplica el archivo).
  const cargarDocsPoliza = async (polizaId) => {
    if (!polizaId) { setPolizaDocs([]); return; }
    const { data } = await supabase.from('documentos')
      .select('id, nombre_archivo, storage_path, tipos_documento(nombre, codigo)')
      .eq('poliza_id', polizaId).eq('activo', true).order('creado_en', { ascending:false });
    const norm = s => (s || '').toLowerCase()
      .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u');
    const quiere = (t) => {
      const cod = (t?.codigo || '').toUpperCase();
      const nom = norm(t?.nombre);
      return cod === 'CP' || cod === 'CG'
        || nom.includes('condiciones generales') || nom.includes('condiciones particulares')
        || nom.includes('poliza') || nom.includes('caratula');
    };
    setPolizaDocs((data || []).filter(d => quiere(d.tipos_documento)));
  };

  // Coberturas afectadas (editable, se usa en crear y en la vista).
  const agregarAfectada = (pcId) => {
    const c = polizaCoberturas.find(x => String(x.id) === String(pcId));
    if (!c || afectadas.some(a => String(a.poliza_cobertura_id) === String(c.id))) return;
    setAfectadas(a => [...a, { poliza_cobertura_id:c.id, nombre:c.nombre, deducible:c.deducible || '', monto_reclamado:'', deducible_monto: deducibleNumerico(c.deducible), monto_aprobado:'' }]);
  };
  // Cobertura MANUAL ("por crear"): para pólizas sin coberturas cargadas — se escribe el nombre a mano.
  const agregarManual = () => setAfectadas(a => [...a, { poliza_cobertura_id:null, manual:true, nombre:'', deducible:'', monto_reclamado:'', deducible_monto:'', monto_aprobado:'' }]);
  const updAfectada = (i, k, v) => setAfectadas(a => a.map((x,j) => j===i ? { ...x, [k]:v } : x));
  const quitarAfectada = (i) => setAfectadas(a => a.filter((_,j) => j!==i));

  const totReclamado = afectadas.reduce((s,a) => s + (num(a.monto_reclamado) || 0), 0);
  const totDeducible = afectadas.reduce((s,a) => s + (num(a.deducible_monto) || 0), 0);
  const totAprobado  = afectadas.reduce((s,a) => s + (num(a.monto_aprobado) || 0), 0);
  const hayMontoReclamado = afectadas.some(a => num(a.monto_reclamado) != null);
  const hayMontoAprobado  = afectadas.some(a => num(a.monto_aprobado) != null);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const guardarAfectadas = async (reclamoId) => {
    await supabase.from('reclamo_coberturas').delete().eq('reclamo_id', reclamoId);
    for (const a of afectadas) {
      if (!a.nombre) continue;
      await insertRow('reclamo_coberturas', {
        reclamo_id: reclamoId, poliza_cobertura_id: a.poliza_cobertura_id || null, nombre: a.nombre || null,
        deducible: a.deducible || null, monto_reclamado: num(a.monto_reclamado), deducible_monto: num(a.deducible_monto), monto_aprobado: num(a.monto_aprobado),
      });
    }
  };

  // Sube archivos adjuntos del reclamo al storage y los enlaza (reclamo_archivos).
  // `items` = [{file, tipo_doc}] o archivos crudos.
  const subirArchivos = async (reclamoId, items) => {
    for (const it of (items || [])) {
      const file = it.file || it;
      const tipo = it.tipo_doc || 'Otro';
      try {
        const ext = (file.name.split('.').pop() || 'dat');
        const path = `reclamos/${reclamoId}/${Date.now()}_${Math.floor(Math.random()*1e4)}.${ext}`;
        const { error } = await supabase.storage.from('solicitud-archivos').upload(path, file, { upsert:true });
        if (error) continue;
        const { data: { publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
        await insertRow('reclamo_archivos', { reclamo_id: reclamoId, nombre: file.name, url: publicUrl, tipo_doc: tipo, tamanio: file.size });
      } catch { /* no bloquear el reclamo si un archivo falla */ }
    }
  };

  const handleSave = async () => {
    if (!form.numero || !form.poliza_id) return alert('Falta la póliza.');
    setSaving(true);
    try {
      const rec = await insertRow('reclamos', {
        numero: form.numero, numero_aseguradora: form.numero_aseguradora || null,
        poliza_id: parseInt(form.poliza_id), cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        aseguradora_id: form.aseguradora_id ? parseInt(form.aseguradora_id) : null,
        descripcion: form.descripcion || null, fecha_siniestro: form.fecha_siniestro || null, fecha_esperada: form.fecha_esperada || null,
        ajustador: form.ajustador || null, fecha_notificacion: form.fecha_notificacion || null,
        estado: form.estado || 'abierto', notas: form.notas || null,
        monto_reclamado: hayMontoReclamado ? totReclamado : null,
        monto_aprobado: hayMontoAprobado ? totAprobado : null,
      });
      if (rec?.id && afectadas.length) await guardarAfectadas(rec.id);
      if (rec?.id && pendingFiles.length) await subirArchivos(rec.id, pendingFiles);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSaving(false); }
  };

  // Guardar el ajustador + la fecha de notificación desde la VISTA (se llenan cuando
  // se envía/notifica el reclamo a la aseguradora, normalmente después de abrirlo).
  const guardarAseg = async () => {
    setSaving(true);
    try {
      const patch = { ajustador: asegEdit.ajustador || null, fecha_notificacion: asegEdit.fecha_notificacion || null };
      await updateRow('reclamos', selected.id, { ...patch, actualizado_en: new Date().toISOString() });
      setSelected(s => ({ ...s, ...patch }));
      await load();
      alert('✓ Datos de la aseguradora guardados.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSaving(false); }
  };

  // Guardar los montos editados desde la VISTA (para llenar los "por determinar").
  const guardarMontosVista = async () => {
    setSaving(true);
    try {
      await guardarAfectadas(selected.id);
      await updateRow('reclamos', selected.id, {
        monto_reclamado: hayMontoReclamado ? totReclamado : null,
        monto_aprobado: hayMontoAprobado ? totAprobado : null,
        actualizado_en: new Date().toISOString(),
      });
      setSelected(s => ({ ...s, monto_reclamado: hayMontoReclamado ? totReclamado : null, monto_aprobado: hayMontoAprobado ? totAprobado : null }));
      await load();
      alert('✓ Montos guardados.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSaving(false); }
  };

  const cambiarEstado = async (r, nuevo) => {
    // No se puede CERRAR sin monto reclamado y aprobado.
    if (nuevo === 'cerrado' && (r.monto_reclamado == null || r.monto_aprobado == null)) {
      return alert('No se puede cerrar el reclamo sin el monto reclamado y el monto aprobado. Llena los montos de las coberturas afectadas primero.');
    }
    try {
      const patch = { estado:nuevo, actualizado_en: new Date().toISOString() };
      if (nuevo === 'cerrado') patch.fecha_cierre = new Date().toISOString().slice(0,10);
      // Reabrir un cerrado: limpia motivo y fecha de cierre.
      if (r.estado === 'cerrado' && nuevo !== 'cerrado') { patch.motivo_cierre = null; patch.fecha_cierre = null; }
      await updateRow('reclamos', r.id, patch);
      await insertRow('reclamos_historial', { reclamo_id:r.id, usuario_id:user?.id, accion:'cambio_estado', detalle:`Estado cambiado a: ${nuevo}` });
      await load();
      if (modal === 'view') {
        setSelected({ ...selected, estado:nuevo, ...(r.estado==='cerrado' && nuevo!=='cerrado' ? { motivo_cierre:null, fecha_cierre:null } : {}) });
        const { data } = await supabase.from('reclamos_historial').select('*, usuarios(nombre)').eq('reclamo_id', r.id).order('creado_en', { ascending:false });
        setHistorial((data || []).map(h => ({ ...h, usuario:h.usuarios?.nombre })));
      }
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // Cierre con MOTIVO: pagado / declinado por la aseguradora, o manual del usuario.
  const confirmarCierre = async () => {
    const motivo = cierre?.motivo;
    if (!motivo) return alert('Elige el motivo de cierre.');
    if (motivo === 'pagado_aseguradora' && !(selected.monto_reclamado > 0 && selected.monto_aprobado > 0)) {
      return alert('Para cerrar como PAGADO, el monto reclamado y el aprobado deben ser MAYORES a 0. Llena los montos de las coberturas y dale "Guardar montos" primero.');
    }
    try {
      const patch = { estado:'cerrado', motivo_cierre:motivo, fecha_cierre:new Date().toISOString().slice(0,10), actualizado_en:new Date().toISOString() };
      if (motivo === 'declinado_aseguradora' && selected.monto_aprobado == null) patch.monto_aprobado = 0;
      await updateRow('reclamos', selected.id, patch);
      await insertRow('reclamos_historial', { reclamo_id:selected.id, usuario_id:user?.id, accion:'cierre', detalle:`Cerrado — ${motivoLabel(motivo)}${cierre.nota ? ': ' + cierre.nota : ''}` });
      setCierre(null); await load();
      setSelected(s => ({ ...s, estado:'cerrado', motivo_cierre:motivo, ...(patch.monto_aprobado != null ? { monto_aprobado:patch.monto_aprobado } : {}) }));
      const { data } = await supabase.from('reclamos_historial').select('*, usuarios(nombre)').eq('reclamo_id', selected.id).order('creado_en', { ascending:false });
      setHistorial((data || []).map(h => ({ ...h, usuario:h.usuarios?.nombre })));
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const eliminarReclamo = async (r) => {
    if (!window.confirm(`¿Eliminar el reclamo ${r.numero}? Se borran sus coberturas, archivos e historial. No se puede deshacer.`)) return;
    try {
      await supabase.from('reclamos_historial').delete().eq('reclamo_id', r.id);
      await supabase.from('reclamos').delete().eq('id', r.id); // cascade: coberturas + archivos
      setModal(null); await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const agregarNota = async () => {
    if (!nota.trim()) return;
    try {
      await insertRow('reclamos_historial', { reclamo_id:selected.id, usuario_id:user?.id, accion:'nota', detalle:nota });
      setNota('');
      const { data } = await supabase.from('reclamos_historial').select('*, usuarios(nombre)').eq('reclamo_id', selected.id).order('creado_en', { ascending:false });
      setHistorial((data || []).map(h => ({ ...h, usuario:h.usuarios?.nombre })));
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const abiertos = rows.filter(r => ['abierto','en_proceso','en_revision'].includes(r.estado)).length;
  // Búsqueda primero: la tabla no muestra reclamos hasta aplicar un filtro.
  const hayFiltro = !!(filterPoliza || filterCliente || filterEstado);
  const polizasDelCliente = form.cliente_id ? polizas.filter(p => String(p.cliente_id) === String(form.cliente_id)) : [];

  // Editor de coberturas afectadas (compartido crear/vista). Se llama como FUNCIÓN
  // (no como <Componente/>) para no remontar los inputs y perder el foco al teclear.
  const renderCoberturas = (editable) => (
    <div style={{ marginTop:6 }}>
      {editable && (
        <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center' }}>
          {polizaCoberturas.length > 0 ? (
            <select className="form-select" defaultValue="" onChange={e=>{ agregarAfectada(e.target.value); e.target.value=''; }} style={{ flex:1 }}>
              <option value="">+ Agregar cobertura afectada…</option>
              {polizaCoberturas.filter(c => !afectadas.some(a => String(a.poliza_cobertura_id) === String(c.id)))
                .map(c => <option key={c.id} value={c.id}>{c.nombre}{c.deducible ? ` — ded. ${c.deducible}` : ''}</option>)}
            </select>
          ) : (
            <span style={{ fontSize:11.5, color:'var(--text-warning)', flex:1 }}>Esta póliza no tiene coberturas cargadas — agrégalas manualmente (por crear) y complétalas después.</span>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={agregarManual}><Plus size={13}/> Cobertura manual</button>
        </div>
      )}
      {afectadas.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--silver)' }}>{editable ? 'Elige arriba las coberturas afectadas por el siniestro.' : 'Sin coberturas afectadas registradas.'}</div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead><tr>
              <th>Cobertura</th><th>Reclamado</th><th>Deducible (póliza)</th><th>Deducible a pagar</th><th>Aprobado</th><th>Neto a pagar</th>{editable && <th></th>}
            </tr></thead>
            <tbody>
              {afectadas.map((a,i) => {
                const neto = (num(a.monto_aprobado) != null) ? (num(a.monto_aprobado) - (num(a.deducible_monto) || 0)) : null;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{editable && a.manual ? <input className="form-input" style={{ width:160 }} placeholder="Nombre de la cobertura" value={a.nombre} onChange={e=>updAfectada(i,'nombre',e.target.value)} /> : a.nombre}</td>
                    <td>{editable ? <input className="form-input" type="number" step="0.01" style={{ width:110 }} placeholder="Por determinar" value={a.monto_reclamado} onChange={e=>updAfectada(i,'monto_reclamado',e.target.value)} /> : fmt$(a.monto_reclamado)}</td>
                    <td className="text-silver" style={{ fontSize:12 }}>{a.deducible || '—'}</td>
                    <td>{editable ? <input className="form-input" type="number" step="0.01" style={{ width:100 }} value={a.deducible_monto} onChange={e=>updAfectada(i,'deducible_monto',e.target.value)} /> : fmt$(a.deducible_monto)}</td>
                    <td>{editable ? <input className="form-input" type="number" step="0.01" style={{ width:110 }} placeholder="Por determinar" value={a.monto_aprobado} onChange={e=>updAfectada(i,'monto_aprobado',e.target.value)} /> : fmt$(a.monto_aprobado)}</td>
                    <td style={{ fontWeight:600, color:'var(--text-success)' }}>{neto != null ? fmt$(neto) : '—'}</td>
                    {editable && <td><button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(239, 68, 68, 0.4)', padding:'3px 7px' }} onClick={()=>quitarAfectada(i)}><Trash2 size={12}/></button></td>}
                  </tr>
                );
              })}
              <tr style={{ fontWeight:700, background:'rgba(var(--steel-rgb),0.35)' }}>
                <td>TOTAL</td>
                <td>{hayMontoReclamado ? fmt$(totReclamado) : <span className="text-silver">Por determinar</span>}</td>
                <td></td>
                <td>{fmt$(totDeducible)}</td>
                <td>{hayMontoAprobado ? fmt$(totAprobado) : <span className="text-silver">Por determinar</span>}</td>
                <td style={{ color:'var(--text-success)' }}>{hayMontoAprobado ? fmt$(totAprobado - totDeducible) : '—'}</td>
                {editable && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Reclamos</div><div className="page-subtitle">{rows.length} total · {abiertos} en proceso</div></div>
        {canEdit('reclamos') && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Nuevo Reclamo</button>}
      </div>

      <div className="toolbar">
        <PolizaSelect value={filterPoliza} onChange={setFilterPoliza} width={220} />
        <ClienteSelect value={filterCliente} onChange={setFilterCliente} placeholder="Filtrar por cliente..." width={220} />
        <select className="form-select" style={{ width:160 }} value={filterEstado} onChange={e=>setFilterEstado(e.target.value)}>
          <option value="">Todos los estados</option>{ESTADOS.map(e => <option key={e} value={e}>{e.replace('_',' ')}</option>)}
        </select>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr><th>N° Reclamo</th><th>N° Aseguradora</th><th>Cliente</th><th>Póliza</th><th>Aseguradora</th><th>Monto Reclamado</th><th>Monto Aprobado</th><th>F. Siniestro</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {!hayFiltro ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon"><Search size={48} /></div><div className="empty-state-title">Busca un reclamo para verlo</div><div>Filtra por póliza, cliente o estado. La lista no muestra todos los reclamos de una vez.</div></div></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon"><AlertTriangle size={48} /></div><div className="empty-state-title">Sin resultados</div></div></td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight:700, color:'var(--text-warning)' }}>{r.numero}</td>
                <td className="text-silver">{r.numero_aseguradora || '—'}</td>
                <td style={{ fontWeight:600 }}>{r.cliente_nombre}</td>
                <td className="text-sky">{r.poliza_num}</td>
                <td className="text-silver">{r.aseguradora_nombre}</td>
                <td className="text-warning">{fmt$(r.monto_reclamado)}</td>
                <td className="text-success">{fmt$(r.monto_aprobado)}</td>
                <td className="text-silver">{fmtFecha(r.fecha_siniestro) || '—'}</td>
                <td>
                  <span className={`badge ${ESTADO_BADGE[r.estado]}`}>{r.estado.replace('_',' ')}</span>
                  {r.estado === 'cerrado' && r.motivo_cierre && <div style={{ fontSize:10.5, color:'var(--silver)', marginTop:2 }}>{motivoCorto[r.motivo_cierre] || r.motivo_cierre}</div>}
                </td>
                <td style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-sm btn-secondary" onClick={()=>openView(r)}>Ver / Gestionar</button>
                  {canEdit('reclamos') && <button className="btn btn-sm btn-secondary" style={{ borderColor:'rgba(239, 68, 68, 0.4)', color:'var(--text-danger)', padding:'3px 8px' }} title="Eliminar" onClick={()=>eliminarReclamo(r)}><Trash2 size={13}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === 'create' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Nuevo Reclamo</div><button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button></div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group"><label className="form-label">N° Reclamo (interno)</label><input className="form-input" value={form.numero} readOnly style={{ opacity:0.8 }} /></div>
                <div className="form-group"><label className="form-label">N° Reclamo de la aseguradora</label><input className="form-input" value={form.numero_aseguradora} onChange={e=>f('numero_aseguradora', e.target.value)} placeholder="El que asigna la compañía" /></div>
                <div className="form-group"><label className="form-label">Cliente *</label><ClienteSelect value={form.cliente_id} onChange={onCliente} placeholder="Buscar cliente..." /></div>
                <div className="form-group"><label className="form-label">Póliza *</label>
                  <select className="form-select" value={form.poliza_id} onChange={e=>onPoliza(e.target.value)} disabled={!form.cliente_id}>
                    <option value="">{form.cliente_id ? 'Seleccionar póliza...' : 'Elige un cliente primero'}</option>
                    {polizasDelCliente.map(p => <option key={p.id} value={p.id}>{p.numero}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">F. Siniestro</label><input className="form-input" type="date" value={form.fecha_siniestro} onChange={e=>f('fecha_siniestro', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">F. Esperada de resolución</label><input className="form-input" type="date" value={form.fecha_esperada} onChange={e=>f('fecha_esperada', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Ajustador de la aseguradora</label><input className="form-input" value={form.ajustador} onChange={e=>f('ajustador', e.target.value)} placeholder="Nombre del ajustador (si ya se asignó)" /></div>
                <div className="form-group"><label className="form-label">F. de envío/notificación del reclamo</label><input className="form-input" type="date" value={form.fecha_notificacion} onChange={e=>f('fecha_notificacion', e.target.value)} /></div>
              </div>

              {form.poliza_id && (
                <div style={{ marginTop:18 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:0.5, marginBottom:2 }}>COBERTURAS AFECTADAS</div>
                  <div style={{ fontSize:11, color:'var(--silver)', marginBottom:6 }}>Elige las coberturas afectadas y su monto. Deja los montos en blanco si aún están <strong>por determinar</strong>; el reclamo no se puede cerrar sin ellos.</div>
                  {renderCoberturas(true)}
                </div>
              )}

              {form.poliza_id && polizaDocs.length > 0 && (
                <div style={{ marginTop:18 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:0.5, marginBottom:2 }}>DOCUMENTOS DE LA PÓLIZA</div>
                  <div style={{ fontSize:11, color:'var(--silver)', marginBottom:6 }}>Se traen de la póliza (Póliza / Condiciones Generales / Particulares) y quedan disponibles en el reclamo.</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {polizaDocs.map(d => (
                      <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                        <Paperclip size={13} style={{ color:'var(--sky-light)' }} />
                        <a href="#" onClick={ev=>{ ev.preventDefault(); abrirDocumento(d.storage_path); }} style={{ color:'var(--sky-light)', textDecoration:'none' }}>{d.nombre_archivo || 'Documento'}</a>
                        {d.tipos_documento?.nombre && <span className="badge badge-neutral text-xs">{d.tipos_documento.nombre}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-grid form-grid-2" style={{ marginTop:18 }}>
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Descripción del reclamo</label><textarea className="form-textarea" value={form.descripcion} onChange={e=>f('descripcion', e.target.value)} placeholder="Qué pasó, detalles del siniestro..." /></div>
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Notas internas</label><textarea className="form-textarea" value={form.notas} onChange={e=>f('notas', e.target.value)} placeholder="Notas de gestión (opcional)..." /></div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Documentos del reclamo</label>
                  <input className="form-input" type="file" multiple onChange={e=>{ const nuevos = Array.from(e.target.files || []).map(file=>({ file, tipo_doc:'Otro' })); setPendingFiles(prev => [...prev, ...nuevos]); e.target.value=''; }} />
                  {pendingFiles.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
                      {pendingFiles.map((pf,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Paperclip size={13} style={{ color:'var(--sky-light)' }} />
                          <span style={{ flex:1, fontSize:12.5 }}>{pf.file.name}</span>
                          <select className="form-select" style={{ width:190 }} value={pf.tipo_doc} onChange={e=>setPendingFiles(prev => prev.map((x,j)=>j===i?{ ...x, tipo_doc:e.target.value }:x))}>
                            {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setPendingFiles(prev=>prev.filter((_,j)=>j!==i))}><X size={12}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>Fotos, FUD, cédula, licencia, cotización, informe del ajustador, etc. — elige el tipo de cada uno.</div>
                </div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button><button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.poliza_id}>{saving ? 'Guardando…' : 'Registrar Reclamo'}</button></div>
          </div>
        </div>
      )}

      {modal === 'view' && selected && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{ color:'var(--text-warning)' }}>{selected.numero}</div>
                <div style={{ display:'flex', gap:8, marginTop:4, alignItems:'center' }}>
                  <span className={`badge ${ESTADO_BADGE[selected.estado]}`}>{selected.estado.replace('_',' ')}</span>
                  {selected.numero_aseguradora && <span className="text-xs text-silver">Aseguradora: {selected.numero_aseguradora}</span>}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-3" style={{ marginBottom:20 }}>
                {[['Cliente',selected.cliente_nombre],['Póliza',selected.poliza_num],['Aseguradora',selected.aseguradora_nombre],['N° Aseguradora',selected.numero_aseguradora],['Ajustador',selected.ajustador],['F. Notificación',fmtFecha(selected.fecha_notificacion)],['Monto Reclamado',fmt$(selected.monto_reclamado)],['Monto Aprobado',fmt$(selected.monto_aprobado)],['F. Siniestro',fmtFecha(selected.fecha_siniestro)],['F. Apertura',fmtFecha(selected.fecha_apertura)],['F. Cierre',fmtFecha(selected.fecha_cierre)],['Motivo de cierre', selected.motivo_cierre ? motivoLabel(selected.motivo_cierre) : null]].map(([l,v]) => (
                  <div key={l}><div className="text-xs text-silver" style={{ marginBottom:2, textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:14 }}>{v || '—'}</div></div>
                ))}
              </div>
              {selected.descripcion && <div style={{ marginBottom:20 }}><div className="text-xs text-silver" style={{ marginBottom:2, textTransform:'uppercase' }}>Descripción</div><div style={{ fontSize:14 }}>{selected.descripcion}</div></div>}

              {/* Ajustador + fecha de notificación (editable: se llenan al enviar el reclamo a la aseguradora) */}
              <div style={{ marginBottom:20, padding:'12px 14px', background:'rgba(var(--sky-rgb),0.06)', border:'1px solid rgba(var(--sky-rgb),0.2)', borderRadius:8 }}>
                <div className="text-xs text-silver" style={{ marginBottom:8, textTransform:'uppercase', fontWeight:700 }}>Gestión con la aseguradora</div>
                <div className="form-grid form-grid-2">
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Ajustador de la aseguradora</label>
                    <input className="form-input" value={asegEdit.ajustador} onChange={e=>setAsegEdit(s=>({ ...s, ajustador:e.target.value }))} placeholder="Nombre del ajustador" disabled={!canEdit('reclamos')} />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">F. de envío/notificación del reclamo</label>
                    <input className="form-input" type="date" value={asegEdit.fecha_notificacion} onChange={e=>setAsegEdit(s=>({ ...s, fecha_notificacion:e.target.value }))} disabled={!canEdit('reclamos')} />
                  </div>
                </div>
                {canEdit('reclamos') && (
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
                    <button className="btn btn-primary btn-sm" onClick={guardarAseg} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
                  </div>
                )}
              </div>

              <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>Coberturas afectadas</div>
              {renderCoberturas(canEdit('reclamos') && selected.estado !== 'cerrado')}
              {canEdit('reclamos') && selected.estado !== 'cerrado' && afectadas.length > 0 && (
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
                  <button className="btn btn-primary btn-sm" onClick={guardarMontosVista} disabled={saving}>{saving ? 'Guardando…' : 'Guardar montos'}</button>
                </div>
              )}

              {/* Documentos traídos de la póliza (Póliza / Condiciones Generales / Particulares) */}
              {polizaDocs.length > 0 && (
                <div style={{ marginTop:22 }}>
                  <div style={{ fontSize:14, fontWeight:700, marginBottom:8 }}>Documentos de la póliza</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {polizaDocs.map(d => (
                      <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                        <Paperclip size={13} style={{ color:'var(--sky-light)' }} />
                        <a href="#" onClick={ev=>{ ev.preventDefault(); abrirDocumento(d.storage_path); }} style={{ color:'var(--sky-light)', textDecoration:'none' }}>{d.nombre_archivo || 'Documento'}</a>
                        {d.tipos_documento?.nombre && <span className="badge badge-neutral text-xs">{d.tipos_documento.nombre}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documentos del reclamo */}
              <div style={{ marginTop:22 }}>
                <div style={{ fontSize:14, fontWeight:700, marginBottom:8 }}>Documentos</div>
                {archivos.length === 0 ? <div className="text-silver text-sm" style={{ marginBottom:8 }}>Sin documentos adjuntos.</div> : (
                  <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:8 }}>
                    {archivos.map(a => (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                        <Paperclip size={13} style={{ color:'var(--sky-light)' }} />
                        <a href="#" onClick={ev=>{ ev.preventDefault(); abrirDocumento(a.url); }} style={{ color:'var(--sky-light)', textDecoration:'none' }}>{a.nombre || 'Documento'}</a>
                        {a.tipo_doc && <span className="badge badge-neutral text-xs">{a.tipo_doc}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {canEdit('reclamos') && (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <select className="form-select" style={{ width:190 }} value={viewDocTipo} onChange={e=>setViewDocTipo(e.target.value)} title="Tipo de documento">
                      {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input className="form-input" type="file" multiple style={{ flex:1 }} onChange={async e=>{
                      const files = Array.from(e.target.files || []); if (!files.length) return;
                      await subirArchivos(selected.id, files.map(file => ({ file, tipo_doc: viewDocTipo }))); e.target.value='';
                      const { data } = await supabase.from('reclamo_archivos').select('*').eq('reclamo_id', selected.id).order('id');
                      setArchivos(data || []);
                    }} />
                  </div>
                )}
              </div>

              {canEdit('reclamos') && (
                <div style={{ margin:'20px 0' }}>
                  <div style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>Acciones:</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
                    {(TRANSICIONES[selected.estado] || []).map(e => <button key={e} className="btn btn-secondary btn-sm" onClick={()=>cambiarEstado(selected, e)}>→ {e.replace('_',' ')}</button>)}
                    {selected.estado !== 'cerrado' && <button className="btn btn-secondary btn-sm" style={{ borderColor:'rgba(var(--sky-rgb),0.5)', color:'var(--sky-light)' }} onClick={()=>setCierre({ motivo:'', nota:'' })}>Cerrar reclamo…</button>}
                    {selected.estado === 'cerrado' && <button className="btn btn-secondary btn-sm" onClick={()=>cambiarEstado(selected, 'en_proceso')}>↺ Reabrir</button>}
                    <button className="btn btn-secondary btn-sm" style={{ borderColor:'rgba(239, 68, 68, 0.5)', color:'var(--text-danger)' }} onClick={()=>eliminarReclamo(selected)}><Trash2 size={13}/> Eliminar</button>
                  </div>
                  {cierre && (
                    <div style={{ marginTop:12, padding:'12px 14px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8 }}>
                      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Cerrar reclamo — ¿por qué se cierra?</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
                        {MOTIVOS_CIERRE.map(([val,lbl]) => (
                          <label key={val} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                            <input type="radio" name="motivo_cierre" checked={cierre.motivo===val} onChange={()=>setCierre(c=>({ ...c, motivo:val }))} style={{ accentColor:'var(--sky)' }} />
                            {lbl}
                          </label>
                        ))}
                      </div>
                      <input className="form-input" placeholder="Nota del cierre (opcional)..." value={cierre.nota} onChange={e=>setCierre(c=>({ ...c, nota:e.target.value }))} style={{ marginBottom:10 }} />
                      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setCierre(null)}>Cancelar</button>
                        <button className="btn btn-primary btn-sm" onClick={confirmarCierre}>Confirmar cierre</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize:14, fontWeight:700, marginBottom:12 }}>Historial de Gestión</div>
              <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                <input className="form-input" placeholder="Agregar nota..." value={nota} onChange={e=>setNota(e.target.value)} style={{ flex:1 }} />
                <button className="btn btn-primary btn-sm" onClick={agregarNota}>Agregar</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:240, overflowY:'auto' }}>
                {historial.length === 0 ? <div className="text-silver text-sm">Sin historial</div> : historial.map((h,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 10px', background:'rgba(var(--steel-rgb),0.25)', borderRadius:6, fontSize:12.5 }}>
                    <span className="badge badge-neutral text-xs" style={{ whiteSpace:'nowrap' }}>{h.accion.replace('_',' ')}</span>
                    <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={h.detalle}>{h.detalle}</span>
                    <span className="text-xs text-silver" style={{ whiteSpace:'nowrap' }}>{h.usuario || ''} · {fmtFechaHora(h.creado_en)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
